/**
 * UranIA PA — Dashboard route (US-011).
 *
 * Punto d'arrivo post-login (US-010 AC#4 `signinRedirect` returnTo
 * default `/dashboard`). Compone i widget operativi (AC#1 letterale):
 *   1. `DashboardLastOnboardingWidget` — ultimo TenantOnboarded
 *      (placeholder finchè US-F1-11 `audit_events` non espone la query).
 *   2. `DashboardTenantInfoWidget` — tenant_id + acr dal JWT claims.
 *   3. Quick action `admin_console` (in `DashboardQuickActionsWidget`)
 *      visibile solo a `platform_admin` / `tenant_admin` (AC#1 "if admin").
 *
 * Quando l'utente non è autenticato (es. apertura diretta del link in
 * preview / scenari di axe testing), la pagina rende un avviso
 * `dashboard-unauthenticated` che richiama al login. La a11y (AC#4) è
 * verificata su entrambi gli stati nello stesso spec (axe-core ignora
 * solo l'eventuale viewport mobile differente).
 */

import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { DashboardLastOnboardingWidget } from '../components/dashboard/DashboardLastOnboardingWidget.tsx';
import { DashboardQuickActionsWidget } from '../components/dashboard/DashboardQuickActionsWidget.tsx';
import { DashboardRiChecklistWidget } from '../components/dashboard/DashboardRiChecklistWidget.tsx';
import { DashboardRiProjectComplianceWidget } from '../components/dashboard/DashboardRiProjectComplianceWidget.tsx';
import { DashboardTenantInfoWidget } from '../components/dashboard/DashboardTenantInfoWidget.tsx';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../lib/auth/auth-store.ts';
import { principalIsAnyAdmin, principalIsPlatformAdmin } from '../lib/auth/rbac.ts';
import { Route as ServiziSegmentRoute } from './_servizi.tsx';

function greetingPrefix(): string {
  const hour = new Date().getHours();
  if (hour < 12) {
    return 'Buongiorno';
  }
  if (hour < 18) {
    return 'Buon pomeriggio';
  }
  return 'Buonasera';
}

function todayDateLabel(): string {
  return new Date().toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);

  if (!isAuthenticated) {
    return (
      <section className="urania-dashboard" data-testid="dashboard-unauthenticated">
        <h1 className="display-5 mb-3">{t('dashboard.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('dashboard.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('dashboard.unauthenticated.message')}</p>
          <Link
            to="/accedi"
            className="btn btn-primary"
            data-testid="dashboard-unauthenticated-go-to-login"
          >
            {t('dashboard.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  const displayName = principal?.preferredUsername ?? principal?.subject ?? '';
  const greeting = greetingPrefix();
  const today = todayDateLabel();
  const roles = principal?.roles ?? [];
  const isRiBeneficiario = roles.includes('beneficiario_ri');
  const isAdmin = principalIsAnyAdmin(principal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);
  const roleLabel = (() => {
    if (roles.includes('platform_admin')) return 'Platform Admin';
    if (roles.includes('tenant_admin')) return 'Tenant Admin';
    if (roles.includes('funzionario_adg')) return 'Funzionario AdG';
    if (roles.includes('beneficiario_ri')) return 'Beneficiario R&I';
    if (roles.includes('auditor')) return 'Auditor';
    return 'Utente';
  })();

  return (
    <section className="urania-dashboard" data-testid="dashboard-page">
      {/* Hero personalizzato */}
      <div
        className="rounded mb-3 px-3 px-md-4 py-2 py-md-3 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1 }}>
          <p
            className="small mb-0 opacity-75"
            data-testid="dashboard-today"
            style={{ fontSize: '0.7rem' }}
          >
            {today.charAt(0).toUpperCase() + today.slice(1)}
          </p>
          <h1 className="h5 mb-1" data-testid="dashboard-greeting">
            {greeting}, <span className="fw-bold">{displayName}</span>
          </h1>
          <p
            className="mb-2 opacity-90 small"
            style={{ maxWidth: 720, fontSize: '0.78rem' }}
          >
            {isAdmin ? t('dashboard.subtitle_admin') : t('dashboard.subtitle')}
          </p>
          <div className="d-flex flex-wrap gap-2">
            <span
              className="badge"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
              }}
            >
              Ruolo: {roleLabel}
            </span>
            {!isPlatformAdmin && principal?.tenantId !== null && principal?.tenantId !== undefined && (
              <span
                className="badge font-monospace"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  fontSize: '0.7rem',
                }}
                title={principal.tenantId}
              >
                Tenant: {principal.tenantId.substring(0, 6)}…{principal.tenantId.substring(principal.tenantId.length - 4)}
              </span>
            )}
            {!isPlatformAdmin && principal?.acr !== null && principal?.acr !== undefined && (
              <span
                className="badge"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  fontSize: '0.7rem',
                }}
              >
                acr: {principal.acr}
              </span>
            )}
          </div>
        </div>
        {/* Decorazione SVG laterale */}
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-20px', right: '-40px', opacity: 0.1 }}
          width="280"
          height="280"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M4 0h5.5L14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm5 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5H9.5A1.5 1.5 0 0 1 8 3.5V1zM3 9a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 9zm0 2.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
        </svg>
      </div>

      <div
        className="urania-dashboard__grid row g-3"
        data-testid="dashboard-widgets-grid"
        role="list"
        aria-label={t('dashboard.widgets_aria_label')}
      >
        <div className="col-12" role="listitem">
          <DashboardQuickActionsWidget />
        </div>
        {isRiBeneficiario && (
          <>
            <div className="col-12 col-lg-6" role="listitem">
              <DashboardRiChecklistWidget />
            </div>
            <div className="col-12 col-lg-6" role="listitem">
              <DashboardRiProjectComplianceWidget />
            </div>
          </>
        )}
        {!isAdmin && (
          <div className="col-12 col-md-6 col-xl-4" role="listitem">
            <DashboardLastOnboardingWidget />
          </div>
        )}
        {!isPlatformAdmin && (
          <div className="col-12 col-md-6 col-xl-4" role="listitem">
            <DashboardTenantInfoWidget />
          </div>
        )}
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => ServiziSegmentRoute,
  path: '/dashboard',
  component: DashboardPage,
});
